import React, { useEffect, useState } from "react";
import axios from "axios";
import { Card, Grid, MenuItem, TextField } from "@mui/material";
import { Navigate } from "react-router-dom";

import Constants from "Helpers/Constants";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";
import MDTypography from "components/MDTypography";

const selectFieldProps = {
  InputLabelProps: { shrink: true },
  sx: {
    "& .MuiOutlinedInput-root": {
      minHeight: 56,
      alignItems: "center",
    },
  },
};

function AutoCreateExamListSets() {
  const [redirect, setRedirect] = useState(false);
  const [loading, setLoading] = useState(false);
  const [allExamLists, setAllExamLists] = useState([]);
  const [notification, setNotification] = useState({
    open: false,
    color: "success",
    message: "",
  });
  const [form, setForm] = useState({
    exam_list_id: "",
    unlock_count: 0,
    sets: "20,20,20",
    status: "inactive",
  });

  const showMessage = (message, color = "success", shouldRedirect = false) => {
    setNotification({ open: true, color, message });
    setTimeout(() => {
      setNotification({ open: false, color, message: "" });
      if (shouldRedirect) setRedirect(true);
    }, 2500);
  };

  useEffect(() => {
    const loadOptions = async () => {
      try {
        const examListResp = await axios.get(Constants.getUrls.getPyqMetaExamLists);
        setAllExamLists(examListResp?.data?.data || []);
      } catch (error) {
        showMessage("Unable to load exam list options.", "error");
      }
    };

    loadOptions();
  }, []);

  const handleChange = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const resp = await axios.post(Constants.postUrls.autoCreateExamListSets, {
        exam_list_id: form.exam_list_id,
        unlock_count: form.unlock_count,
        sets: form.sets,
        status: form.status,
      });
      showMessage(resp?.data?.message || "Exam sets created successfully.", "success", true);
    } catch (error) {
      showMessage(error?.response?.data?.message || "Unable to auto create exam sets.", "error");
    } finally {
      setLoading(false);
    }
  };

  if (redirect) return <Navigate to="/exam-list-sets" />;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Card>
          <MDBox
            mx={2}
            mt={-3}
            py={3}
            px={2}
            variant="gradient"
            bgColor="info"
            borderRadius="lg"
            coloredShadow="info"
          >
            <MDTypography variant="h6" color="white">
              Auto Create Exam Sets
            </MDTypography>
          </MDBox>
          <MDBox py={3} px={2}>
            <form onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    fullWidth
                    label="Exam List"
                    value={form.exam_list_id}
                    onChange={(e) => handleChange("exam_list_id", e.target.value)}
                    {...selectFieldProps}
                  >
                    <MenuItem value="">Select Exam List</MenuItem>
                    {allExamLists.map((item) => (
                      <MenuItem key={item._id} value={item._id}>
                        {item?.exam_id?.exam_name || "Exam"} • {item?.year_id?.year || "Year"} • {item.exam_name}
                        {item?.shift ? ` • ${item.shift}` : ""}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    type="number"
                    fullWidth
                    label="Starting Unlocked Sets"
                    value={form.unlock_count}
                    onChange={(e) => handleChange("unlock_count", e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    select
                    fullWidth
                    label="Status"
                    value={form.status}
                    onChange={(e) => handleChange("status", e.target.value)}
                    {...selectFieldProps}
                  >
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                    <MenuItem value="active_for_admin">Active For Admin</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <MDTypography variant="caption" color="text" display="block" mb={1}>
                    Enter set question counts separated by commas. Example: 20,20,15,25.
                    Time will be auto-set at 60 seconds per question, so 20 questions becomes 20 minutes.
                  </MDTypography>
                  <TextField
                    fullWidth
                    multiline
                    minRows={4}
                    label="Set Question Counts"
                    value={form.sets}
                    onChange={(e) => handleChange("sets", e.target.value)}
                    placeholder="20,20,15"
                  />
                </Grid>
              </Grid>
              <Grid align="left" mt={2}>
                <MDButton variant="contained" size="small" color="info" type="submit">
                  {loading ? "Creating..." : "Create Sets"}
                </MDButton>
              </Grid>
            </form>
          </MDBox>
          <MDSnackbar
            color={notification.color}
            icon="notifications"
            title="AgriQuiz"
            content={notification.message}
            dateTime="Just now"
            open={notification.open}
          />
        </Card>
      </MDBox>
    </DashboardLayout>
  );
}

export default AutoCreateExamListSets;
